// The person on a SMART ROOM that is occupied — and nothing at all otherwise
// (unoccupied or unknown), as the owner asked (2026-10-11). Both shells.
// + a red 🔒 when the room is LOCKED (2026-10-12): every user sees it, wherever
// the room is listed.
// + VACANT (2026-10-15): a LOCKED room with no motion from 5 s to 300 s after the
// lock gets a crossed-out person — "no one inside". The rule is
// core/devices/roomPresence lockVerdict; motion after the lock brings the person back.
// The verdict is the server's: core/devices/roomPresence, core/hooks/useRoomPresence.
//
// `tip` adds the "motion 2 min ago" caption on hover / tap. Leave it off where
// the row underneath has its own long-press (the phone's room list unmaps on
// a long-press), or the two gestures fight.

import React from 'react';
import { Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import LockIcon from '@mui/icons-material/Lock';
import {
  isOccupied, lockCaption, presenceCaption, vacantCaption, roomVacancy, vacantFor,
} from '../../core/devices/roomPresence';

export const OCCUPIED_GREEN = '#43A047';

export const LOCKED_RED = '#E53935';

export const VACANT_BLUE = '#90A4AE';

export default function RoomPresenceBadge({ presence, tip = false, size = 'small', sx }) {
  if (!presence) return null;
  const now = Date.now();
  const occupied = isOccupied(presence, now);
  // Vacant whether locked or not (2026-10-18) — roomVacancy, with since-when.
  const vac = roomVacancy(presence, now);
  const vacant = vac.vacant;
  if (!occupied && !vacant && !presence.locked) return null;

  const lock = presence.locked && (
    <LockIcon fontSize={size} aria-label="Locked" sx={{ color: LOCKED_RED, flexShrink: 0, ...sx }} />
  );
  const who = (occupied && (
    <PersonIcon fontSize={size} aria-label="Occupied" sx={{ color: OCCUPIED_GREEN, flexShrink: 0, ...sx }} />
  )) || (vacant && (
    <PersonOffIcon fontSize={size} aria-label="Vacant" sx={{ color: VACANT_BLUE, flexShrink: 0, ...sx }} />
  ));
  if (!who) {
    return tip ? <Tooltip title={lockCaption(presence)} enterTouchDelay={0} arrow>{lock}</Tooltip> : lock;
  }
  const both = <>{lock}{who}</>;
  if (!tip) return both;
  const title = [
    presence.locked && lockCaption(presence, now),
    occupied ? presenceCaption(presence, now)
      : (vacantCaption(presence, now) || (vac.forMs != null ? `Vacant — ${vacantFor(vac.forMs)}` : 'Vacant')),
  ].filter(Boolean).join(' — ');
  return (
    <Tooltip title={title} enterTouchDelay={0} arrow>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{both}</span>
    </Tooltip>
  );
}
